import * as Application from 'expo-application';
import React, { useEffect } from 'react';
import { Platform } from 'react-native';

import { useListTeamsQuery } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { getStoredUtmProperties } from '@utils/utmParams';

import { useMixpanel } from './MixpanelContext';

const MixpanelGlobalProperties: React.FC = () => {
  const { mixpanel } = useMixpanel();
  const { me, isPaid } = useMe();

  const { data: teamsData } = useListTeamsQuery({
    fetchPolicy: 'cache-first',
    skip: !me?.id,
  });

  useEffect(() => {
    if (mixpanel) {
      const globalProperties: Record<string, any> = {
        device_type: Platform.OS,
        platform: Platform.OS,
        // nativeApplicationVersion is null on web, where there is no
        // store-facing version to report
        app_version: Application.nativeApplicationVersion ?? 'web',
      };

      if (me?.id) {
        globalProperties.user_id = me.id;
        mixpanel.identify(me.id);

        const primaryTeam = teamsData?.listTeams?.find(
          (t) => t.id === me.primaryTeamId
        );

        const peopleProps: Record<string, any> = {
          $email: me.email,
          $name: me.name,
          signup_date: me.createdAt,
          team_count: me.teams?.length ?? 0,
          plan_type: isPaid ? 'paid' : 'free',
        };
        // Only set team_size / role when the primary team is known to
        // avoid sending undefined values that corrupt the Mixpanel profile.
        if (primaryTeam?.usersCount != null) {
          peopleProps.team_size = primaryTeam.usersCount;
        }
        if (primaryTeam?.currentUserRole != null) {
          peopleProps.role = primaryTeam.currentUserRole;
        }
        mixpanel.getPeople().set(peopleProps);

        if (Platform.OS === 'web') {
          const utmProps = getStoredUtmProperties();
          const definedUtmProps = Object.fromEntries(
            Object.entries(utmProps).filter(([, v]) => v !== undefined)
          );
          if (Object.keys(definedUtmProps).length > 0) {
            mixpanel.getPeople().set(definedUtmProps);
          }
        }
      }

      if (Platform.OS === 'web') {
        globalProperties.browser = navigator.userAgent;
      }

      mixpanel.registerSuperProperties(globalProperties);
    }
  }, [mixpanel, me, teamsData, isPaid]);

  return null;
};

export default MixpanelGlobalProperties;
